import React, { useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useGamificationContext } from '../../context/GamificationContext';
import {
  createChart,
  ColorType,
  IChartApi,
  ISeriesApi,
  Time,
  AreaSeries,
} from 'lightweight-charts';
import ChartShareButton from '../Common/ChartShareButton';

interface ChartData {
  time: string; // 'yyyy-mm-dd'
  value: number;
}

interface TradingViewChartProps {
  data: ChartData[];
  height?: number;
  colors?: {
    backgroundColor?: string;
    lineColor?: string;
    textColor?: string;
    areaTopColor?: string;
    areaBottomColor?: string;
  };
}



// ...

const TradingViewChart: React.FC<TradingViewChartProps> = ({
  data,
  height = 300,
  colors,
}) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const { trackAction } = useGamificationContext();
  const { token } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const activeColors = {
    backgroundColor: colors?.backgroundColor || (isDark ? '#0a2128' : '#ffffff'),
    lineColor: colors?.lineColor || '#2962FF',
    textColor: colors?.textColor || (isDark ? '#d3e2e8' : '#333333'),
    areaTopColor: colors?.areaTopColor || '#2962FF',
    areaBottomColor: colors?.areaBottomColor || 'rgba(41, 98, 255, 0.28)',
    gridColor: isDark ? 'rgba(211, 226, 232, 0.05)' : 'rgba(156, 163, 175, 0.1)',
  };

  useEffect(() => {
    if (token) {
      trackAction('VIEW_CHART');
    }
  }, [token, trackAction]);

  useEffect(() => {
    if (!chartContainerRef.current) return;

    const handleResize = () => {
      if (chartRef.current && chartContainerRef.current) {
        chartRef.current.applyOptions({ width: chartContainerRef.current.clientWidth });
      }
    };

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: activeColors.backgroundColor },
        textColor: activeColors.textColor,
      },
      width: chartContainerRef.current.clientWidth,
      height: height,
      grid: {
        vertLines: { visible: false },
        horzLines: { color: activeColors.gridColor },
      },
      rightPriceScale: {
        borderVisible: false,
      },
      timeScale: {
        borderVisible: false,
      },
    });

    chartRef.current = chart;

    const areaSeries = chart.addSeries(AreaSeries, {
      lineColor: activeColors.lineColor,
      topColor: activeColors.areaTopColor,
      bottomColor: activeColors.areaBottomColor,
    });

    // Ensure data is sorted by time and in correct format
    const sortedData = [...data].sort(
      (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime()
    );

    // Cast to unknown then to the specific type expected by setData if needed,
    // but lightweight-charts usually accepts { time: string, value: number }[]
    areaSeries.setData(sortedData as any);

    chart.timeScale().fitContent();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
    };
  }, [data, height, colors, theme]);

  return (
    <div style={{ position: 'relative', width: '100%', height: `${height}px` }}>
      <div ref={chartContainerRef} style={{ width: '100%', height: '100%' }} />
      <ChartShareButton
        targetRef={chartContainerRef}
        fileName="chart_tradingview"
        style={{ position: 'absolute', top: 10, right: 10, zIndex: 10 }}
      />
    </div>
  );
};

export default TradingViewChart;

